import type {
    FormField,
    ReactiveDropdownCatalogPhase,
    ReactiveDropdownCatalogs,
} from '../../interfaces/form.interface';

export type { ReactiveDropdownCatalogPhase };

export interface NormalizedReactiveDropdownCatalogs {
    readonly critical: readonly string[];
    readonly deferred: readonly string[];
    readonly lazy: readonly string[];
}

const EMPTY_REACTIVE_DROPDOWN_CATALOGS: NormalizedReactiveDropdownCatalogs = {
    critical: [],
    deferred: [],
    lazy: [],
};

/**
 * Normalizes catalog field names once so phase checks stay cheap and deterministic.
 */
export function normalizeReactiveDropdownCatalogs(
    catalogs: ReactiveDropdownCatalogs | null | undefined,
): NormalizedReactiveDropdownCatalogs {
    if (!catalogs) {
        return EMPTY_REACTIVE_DROPDOWN_CATALOGS;
    }

    return {
        critical: normalizeCatalogFieldNames(catalogs.critical),
        deferred: normalizeCatalogFieldNames(catalogs.deferred),
        lazy: normalizeCatalogFieldNames(catalogs.lazy),
    };
}

/**
 * Resolves the loading phase for a dropdown field.
 *
 * Explicit catalogs win over legacy dataSource.lazy. When a field appears in multiple phases, the safest phase wins:
 * critical, then deferred, then lazy. This prevents a typo in a secondary list from silently starving critical forms.
 */
export function resolveReactiveDropdownCatalogPhase<T>(
    field: FormField<T>,
    catalogs: ReactiveDropdownCatalogs | null | undefined,
): ReactiveDropdownCatalogPhase {
    const fieldName = String(field.name ?? '').trim();
    const normalizedCatalogs = normalizeReactiveDropdownCatalogs(catalogs);

    if (fieldName !== '' && normalizedCatalogs.critical.includes(fieldName)) {
        return 'critical';
    }

    if (fieldName !== '' && normalizedCatalogs.deferred.includes(fieldName)) {
        return 'deferred';
    }

    if (fieldName !== '' && normalizedCatalogs.lazy.includes(fieldName)) {
        return 'lazy';
    }

    return field.dataSource?.lazy === true ? 'lazy' : 'critical';
}

export function filterReactiveDropdownCatalogPhase<T>(
    fields: readonly FormField<T>[],
    catalogs: ReactiveDropdownCatalogs | null | undefined,
    phase: ReactiveDropdownCatalogPhase,
): FormField<T>[] {
    return fields.filter((field) => resolveReactiveDropdownCatalogPhase(field, catalogs) === phase);
}

function normalizeCatalogFieldNames(fieldNames: readonly string[] | undefined): readonly string[] {
    if (!fieldNames) {
        return [];
    }

    return Array.from(new Set(
        fieldNames
            .map((fieldName) => fieldName.trim())
            .filter((fieldName) => fieldName !== ''),
    ));
}
